<template>
  <div class="bg-light-gray px-4">
    <div class="row">
      <div class="col-12 mb-3 ">
        <icon-view
          :has-border="false"
          icon="fas fa-calendar-alt text-orange"
          icon-size="17px"
          variant="custom-icon-view"
          icon-background="white"
          title="Coordinar Cita"
        />
      </div>
    </div>
    <validation-observer ref="appointmentBaseInfoValidator">
      <div class="row">
        <div class="col-lg-3 col-md-6 col-sm-12">
          <validation-provider
            v-slot="{ errors }"
            :custom-messages="{
              required: 'Favor seleccione la fecha',
            }"
            rules="required"
            name="coordinateAppointment.date"
          >
            <custom-date-picker
              required
              :disabled-days="disabledDays"
              v-model="coordinateAppointment.date"
              label="Fecha:"
              placeholder="DD/MM/YYYY"
              class="custom-datetime-picker-container"
              :container-class="'w-100'"
              :error="errors[0]"
              :min-date="getMinDate"
              :custom-error-msg.sync="errors[0]"
            />
          </validation-provider>
        </div>
        <div class="col-lg-3 col-md-6 col-sm-12 mt-2 mt-md-0">
          <validation-provider
            v-slot="{ errors }"
            :custom-messages="{
              required: 'Favor seleccionar hora desde',
            }"
            rules="required"
            name="coordinateAppointment.timeFrom"
          >
            <custom-time-picker
              label="Desde:"
              label-class="input-label"
              v-model="coordinateAppointment.timeFrom"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
              ref="fromHour"
            />
          </validation-provider>
        </div>
        <div class="col-lg-3 col-md-6 col-sm-12 mt-2 mt-md-0">
          <validation-provider
            v-slot="{ errors, failedRules }"
            :rules="
              `required|timeGreaterThan:${coordinateAppointment.timeFrom}`
            "
            name="coordinateAppointment.timeTo"
          >
            <custom-time-picker
              label="Hasta:"
              label-class="input-label"
              v-model="coordinateAppointment.timeTo"
              :error="errors[0]"
              :custom-error-msg="
                failedRules['timeGreaterThan']
                  ? 'La hora hasta debe ser mayor que la hora de inicio.'
                  : 'La hora hasta es requerida.'
              "
            />
          </validation-provider>
        </div>
        <div
          v-if="requestReferral"
          class="col-lg-3 col-md-6 col-sm-12 mt-2 mt-md-0"
        >
          <validation-provider
            v-slot="{ errors }"
            :custom-messages="{
              required: 'Favor seleccionar número de referido',
            }"
            rules="required"
          >
            <custom-auto-complete
              label="Número de Referido:"
              v-model="referralNumber"
              ref="referralAutocompleteRef"
              placeholder="Escriba Número de Referido"
              v-on="$listeners"
              :debounce-time="2000"
              :get-result-value="getReferralResultValue"
              :search="searchReferralAutoCompleteHandler"
              :class="'w-100'"
              @changed="handleOnReferralChange"
              @submit="handleReferralSubmit"
              :error="errors[0]"
              :custom-error-msg.sync="errors[0]"
            />
          </validation-provider>
        </div>
      </div>
    </validation-observer>
    <div class="row mt-3">
      <div class="col-lg-6 col-md-6 col-sm-12 mt-2">
        <custom-drop-down
          label="Participante(s):"
          fieldkey="userId"
          fieldname="fullName"
          :options="specialistsFiltered"
          placeholder="Seleccione"
          v-model="specialist"
          @input="handleSubmit"
        />
      </div>
      <div class="col-lg-3 col-md-6 col-sm-12 mt-2">
        <custom-drop-down
          label="CAAT:"
          fieldkey="id"
          fieldname="name"
          :disabled="specialistsCaatsFiltered.length <= 1"
          :options="specialistsCaatsFiltered"
          placeholder="Seleccione"
          v-model="caat"
        />
      </div>
      <div class="col-lg-2 pt-2 col-md-6 col-sm-12 mt-2">
        <action-button
          class="mt-4"
          variant="success"
          :i="`fas fa-file-plus pr-1`"
          :disabled="caat == null"
          @click="addParticipantToList"
        >
          Añadir Participante
        </action-button>
      </div>
    </div>
    <div class="row">
      <div class="col-12 mb-3 ">
        <icon-view
          class="mt-4"
          :has-border="false"
          icon="fas fa-user-alt text-orange"
          icon-size="17px"
          variant="custom-icon-view"
          icon-background="white"
          title="Participantes"
        />
      </div>
    </div>
    <div class="row p-2 pt-0">
      <div
        v-for="(participant, index) in selectedParticipantsList"
        class="bg-white p-3 m-2 col-md-3"
        :key="index"
      >
        <div class="text-right">
          <span
            @click="removeParticipantFromList(participant.userId)"
            class="cursor-pointer"
          >
            x
          </span>
        </div>
        <strong>{{ participant.fullName }}</strong>
        <p class="mt-2">
          {{ participant.roleName }} / {{ participant.companyName }}
        </p>
      </div>
    </div>
    <div class="d-flex flex-row justify-content-end w-100 pr-3 pb-4">
      <action-button
        id="btnCancel"
        variant="outline-danger mr-2"
        type="button"
        i="fas fa-times-circle"
        @click="hideCoordinateAppointmentSection()"
      >
        Cancelar
      </action-button>
      <action-button
        id="btnAdd"
        variant="success"
        i="fas fa-calendar-alt"
        :disabled="allowCoordinateAppointment"
        @click="handleCoordinateAppointment()"
      >
        Coordinar
      </action-button>
    </div>
  </div>
</template>

<script>
import referralAndEquipmentsservice from '@/services/api/referralsandequipments.api';
import appointmentCalendarService from '@/services/api/appointmentcalendar.api';
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import { CustomAutoComplete } from '@/components/common';
import CustomDatePicker from '@/components/common/form/CustomDatePicker.vue';
import CustomTimePicker from '@/components/common/CustomTimePicker.vue';
import mixAlert from '@/mixins/alert.mixin';
import IconView from 'icon-view';
import utils from '@/utils/utilities';

export default {
  components: {
    ActionButton,
    CustomDropDown,
    CustomAutoComplete,
    CustomTimePicker,
    CustomDatePicker,
    IconView,
  },
  mixins: [mixAlert],
  props: {
    referral: {
      type: Object,
      default: () => ({}),
    },
    requestReferral: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      referralNumber: null,
      caat: null,
      specialist: null,
      coordinateAppointment: {
        date: null,
        timeFrom: null,
        timeTo: null,
        participant: null,
        caat: {},
      },
      searchParticipantSnapshot: {
        data: [],
      },
      specialistsFiltered: [],
      specialistsCaatsFiltered: [],
      selectedParticipantsList: [],
      searchResponse: [],
      searchReferralSnapshot: {
        data: [],
      },
      referralsFiltered: [],
      selectedReferralsList: [],
      referralsSearchResponse: [],
      disabledDays: [],
    };
  },
  computed: {
    allowCoordinateAppointment() {
      return this.selectedParticipantsList.length == 0;
    },
    getMinDate() {
      return utils.yesterday();
    },
  },
  async mounted() {
    await this.getCalendarDefinedTime();
    await this.getAllParticipants();
  },
  methods: {
    async getCalendarDefinedTime() {
      const {
        data,
      } = await appointmentCalendarService.getDefinedCalendarTime();
      this.disabledDays = data?.days?.split(',');
    },
    async handleCoordinateAppointment() {
      var isValid = await this.$refs.appointmentBaseInfoValidator.validate();
      if (!isValid) {
        return false;
      }
      if (this.selectedParticipantsList.length == 0) {
        this.ShowToast(
          'Alerta',
          'Para coordinar una cita, debe seleccionar al menos un participante.',
          'error'
        );
        this.clearSearchModel();
        return false;
      }

      const caatId = this.requestReferral
        ? this.searchReferralSnapshot.caatId
        : this.referral.caatId;

      if (!this.selectedParticipantsList.some((x) => x.caatId == caatId)) {
        this.ShowToast(
          'Alerta',
          'Debe existir un participante dentro del CAAT de coordinación.',
          'error'
        );
        this.clearSearchModel();
        return false;
      }
      var payload = {
        serviceId: this.requestReferral
          ? this.searchReferralSnapshot.serviceId
          : this.referral.id,
        date: this.coordinateAppointment.date,
        timeFrom: this.coordinateAppointment.timeFrom,
        timeTo: this.coordinateAppointment.timeTo,
        studentSieId: this.requestReferral
          ? this.searchReferralSnapshot.studentSie
          : this.referral.sieId,
        participants: this.selectedParticipantsList.map((item) => ({
          userId: item.userId,
          roleId: item.roleId,
          isATEvaluator: item.isATEvaluator,
          companyId: item.companyId,
          specialistLocationId: item.specialistLocationId,
          specialistId: item.specialistId,
          caatId: item.caatId,
        })),
      };
      referralAndEquipmentsservice
        .coordinateAppointment(payload)
        .then(async () => {
          this.referral.isOpen = false;
          this.ShowToast('', 'Cita coordinada exitosamente.', 'success');
          this.clearModel();
          this.clearSearchModel();
          if (this.requestReferral) {
            this.clearReferralSearchModel();
          }
          this.$emit('save');
          await this.$refs.appointmentBaseInfoValidator.reset();
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    clearModel() {
      this.coordinateAppointment = {
        date: null,
        timeFrom: null,
        timeTo: null,
        participants: [],
        caat: {},
      };
    },
    clearSearchModel() {
      this.searchParticipantSnapshot = {
        data: [],
      };
      this.specialistsCaatsFiltered = [];
      this.caat = null;
      this.specialist = null;
    },
    clearReferralSearchModel() {
      this.searchReferralSnapshot = {
        data: [],
      };
      this.specialistsCaatsFiltered = [];
      this.referralsFiltered = [];
      this.referralNumber = null;
      this.$refs.referralAutocompleteRef.onClearHandler();
    },
    addParticipantToList() {
      if (
        this.selectedParticipantsList.some(
          (x) =>
            (this.searchParticipantSnapshot.specialistId &&
              x.specialistId == this.searchParticipantSnapshot.specialistId) ||
            (this.searchParticipantSnapshot.userId &&
              x.userId == this.searchParticipantSnapshot.userId)
        )
      ) {
        this.ShowToast(
          'Alerta',
          'El participante ya se encuentra en la lista.',
          'error'
        );
        this.clearSearchModel();
        return false;
      }
      this.selectedParticipantsList.push({
        specialistId: this.searchParticipantSnapshot.specialistId,
        userId: this.searchParticipantSnapshot.userId,
        roleId: this.searchParticipantSnapshot.roleId,
        companyId: this.searchParticipantSnapshot.companyId,
        companyName: this.searchParticipantSnapshot.companyName,
        fullName: this.searchParticipantSnapshot.input,
        roleName: this.searchParticipantSnapshot.roleName,
        caatId: this.caat.id,
        specialistLocationId: this.caat.specialistLocationId,
        isATEvaluator: this.searchParticipantSnapshot.isATEvaluator,
      });
      this.clearSearchModel();
    },
    removeParticipantFromList(userId) {
      this.selectedParticipantsList = this.selectedParticipantsList.filter(
        (x) => x.userId != userId
      );
    },
    getResultValue(result) {
      return result.fullName;
    },
    getReferralResultValue(result) {
      return result.referralNumber;
    },
    async handleSubmit(val) {
      this.caat = null;
      this.specialistsCaatsFiltered = [];

      if (!val) {
        return;
      }

      this.coordinateAppointment.participant = val.fullName;
      this.searchParticipantSnapshot.input = val.fullName;
      this.searchParticipantSnapshot.specialistId = val.specialistId;
      this.searchParticipantSnapshot.userId = val.userId;
      this.searchParticipantSnapshot.companyId = val.companyId;
      this.searchParticipantSnapshot.companyName = val.companyName;
      this.searchParticipantSnapshot.roleName = val.roleName;
      this.searchParticipantSnapshot.roleId = val.roleId;
      this.searchParticipantSnapshot.isATEvaluator = val.isATEvaluator;

      this.specialistsCaatsFiltered = [];
      var caats = this.searchResponse.filter(
        (x) =>
          x.userId == val.userId &&
          x.specialistId == val.specialistId &&
          x.roleId == val.roleId
      );
      caats.forEach((caat) => {
        this.specialistsCaatsFiltered.push({
          id: caat.caatId,
          name: caat.caatName,
          specialistLocationId: caat.specialistLocationId,
        });
      });
      if (this.specialistsCaatsFiltered.length == 1) {
        this.caat = this.specialistsCaatsFiltered[0];
      }
    },
    async handleReferralSubmit(val) {
      this.searchReferralSnapshot.input = val.fullName;
      this.searchReferralSnapshot.referralNumber = val.referralNumber;
      this.searchReferralSnapshot.studentSie = val.studentSie;
      this.searchReferralSnapshot.serviceId = val.serviceId;
      this.searchReferralSnapshot.caatId = val.caatId;
      this.referralNumber = val.referralNumber;
    },
    handleOnReferralChange(val) {
      this.referralNumber = val.referralNumber;
      if (!val) {
        this.searchReferralSnapshot.input = null;
      }
    },
    getAllParticipants() {
      return new Promise((resolve) => {
        referralAndEquipmentsservice
          .getAllParticipants('')
          .then(({ data: response }) => {
            this.specialistsFiltered = [];
            this.searchResponse = response;
            response.forEach((element) => {
              var especialistExists = this.specialistsFiltered.some(
                (x) =>
                  x.userId == element.userId &&
                  x.specialistId == element.specialistId &&
                  x.roleId == element.roleId
              );
              if (!especialistExists) {
                this.specialistsFiltered.push({
                  fullName: element.fullName,
                  userId: element.userId,
                  specialistId: element.specialistId,
                  roleName: element.roleName,
                  companyName: element.companyName,
                  companyId: element.companyId,
                  roleId: element.roleId,
                  isATEvaluator: element.isATEvaluator,
                });
              }
            });

            this.searchParticipantSnapshot.data = this.specialistsFiltered.map(
              (item) => ({
                ...item,
                fullName: item.fullName,
                sub1: item.roleName,
                sub2: item.companyName,
              })
            );
            resolve(this.searchParticipantSnapshot.data);
          });
      });
    },
    searchReferralAutoCompleteHandler(input) {
      return new Promise((resolve) => {
        if (input.length < 4) {
          return resolve([]);
        }

        if (input == this.searchReferralSnapshot.input) {
          return resolve(this.searchReferralSnapshot.data);
        }
        this.searchReferralSnapshot.input = input;
        referralAndEquipmentsservice
          .getReferralByNumber(input)
          .then(({ data: response }) => {
            this.referralsFiltered = [];
            this.searchReferralSnapshot.data = response.map((item) => ({
              ...item,
              fullName: item.fullName,
              sub1: item.referralNumber,
              sub2: item.studentSie,
            }));
            resolve(this.searchReferralSnapshot.data);
          });
      });
    },
    hideCoordinateAppointmentSection() {
      this.$emit('cancel');
    },
  },
};
</script>
<style>
.vdp-datepicker__calendar .disabled {
  color: #ddd !important;
  cursor: default;
}
</style>
<style lang="scss" scoped>
div.custom-callout div.bg-secondary-3 {
  border-bottom: solid #ffffff 2px !important;
}
::v-deep .width-container {
  width: 100% !important;
}

.custom-datetime-picker-container ::v-deep .menu-right {
  right: auto !important;
}
::v-deep .dot {
  padding: 19px !important;
}
</style>
